import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useTheme } from "../hooks/useTheme";
import SkeletonSelect from "./skeletons/SkeletonSelect";
import { useLockState } from "./userLocks/LockCheck";

const CustomSelect = ({ value, options, isMulti = false, sortItems = true, onChange, invalid, styles: extraStyles, ...props }) => {
    const themeString = useTheme();
    const lockState = useLockState();


    const [isLoading, setIsLoading] = useState(true);

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
        indicatorSeparator: (styles) => ({
            ...styles,
            backgroundColor: "transparent",
        }),
    };

    const customTheme = (reactSelectTheme) => ({
        ...reactSelectTheme,
        colors: {
            ...reactSelectTheme.colors,
            primary25: "#B2D953",
            primary: themeString === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: themeString === "dark-mode" ? "#1b272d" : "#E4E4E4",                                           // control background  (bg-color)
            neutral5: themeString === "dark-mode" ? "#283941" : "#eceff1",                                          // disabled control background  (bg-color-contrast5)
            neutral10: themeString === "dark-mode" ? "#2c414b" : reactSelectTheme.colors.neutral10,                 // multi-value tag background  (bg-color-contrast2)
            neutral20: themeString === "dark-mode" ? "rgba(255,255,255,0.2)" : reactSelectTheme.colors.neutral20,   // border  (border-color)
            neutral30: themeString === "dark-mode" ? "rgba(255,255,255,0.35)" : reactSelectTheme.colors.neutral30,  // border hover  (border-color2 approx)
            neutral40: themeString === "dark-mode" ? "rgba(234,242,251,0.78)" : "rgba(53,54,59,0.78)",              // disabled text & indicators  (text-color-muted)
            neutral50: themeString === "dark-mode" ? "rgba(234,242,251,0.78)" : "rgba(53,54,59,0.78)",              // placeholder text  (text-color-muted)
            neutral60: themeString === "dark-mode" ? "rgba(255,255,255,0.6)" : reactSelectTheme.colors.neutral60,   // dropdown indicators
            neutral80: themeString === "dark-mode" ? "#eaf2fb" : "#35363b",                                         // selected/input text  (text-color)
        },
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000); // 2 seconds delay

        return () => clearTimeout(timer);
    }, []);

    // If options are empty (loading), display the skeleton placeholder
    if (!options || options.length === 0) {
        if (isLoading) {
            return <SkeletonSelect />;
        }
        else {
            return <p>No options available</p>;
        }
    }

    // Sort options alphabetically by label
    const sortedOptions = Array.isArray(options[0]?.options)
        ? options.map(group => ({
            ...group,
            options: [...group.options].sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            )
        }))
        : [...options].sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        );


    var selectedValue = null

    // Ensure `value` is an object from `options`
    if (options[0].options !== undefined) {
        // Grouped list of options

        for (const group of options) {
            const found = group.options.find(opt => opt.value === value);
            if (found) {
                selectedValue = found
            }
        }
    } else {
        // Single level list of options
        selectedValue = sortedOptions.find((option) => option.value === value) || null;
    }

    return (
        <Select
            {...props}
            options={sortItems ? sortedOptions : options}
            isMulti={isMulti}
            menuPortalTarget={document.body}
            isSearchable
            menuPosition="fixed"
            styles={{
                ...customStyles,
                ...extraStyles,
                menuPortal: base => ({ ...base, zIndex: 9999, ...(extraStyles?.menuPortal?.(base)) }),
                menu: base => ({ ...base, zIndex: 9999, ...(extraStyles?.menu?.(base)) })
            }}
            classNamePrefix="data-select"
            theme={customTheme}
            onChange={onChange}
            value={isMulti ? undefined : selectedValue}
            className={(invalid ? 'data-select--is-invalid' : '') + " " + (lockState?.isLocked ? "locked" : "")}
        />
    );
};

export default CustomSelect;